/**
 * useImpersonateUserFlow — start impersonating a user, correctly.
 *
 * The mutation hook (`useImpersonateUser`) only swaps the session cookie. The
 * surrounding dance is the part that's easy to get wrong, and the ORDER MATTERS:
 *
 *   1. impersonate            — backend swaps the session_id cookie
 *   2. resetClientState       — wipe admin-scoped React Query cache, or the
 *                               impersonated user briefly sees admin data
 *   3. navigate('/dashboard') — BEFORE flipping the auth store. If the store
 *                               flips first, the route guard sees a plain
 *                               "user" role still sitting on an admin route and
 *                               races us to /unauthorized.
 *   4. validateSession + setAuthData — adopt the new identity
 *
 * This flow is hand-rolled identically in ManageUsersPage, EntriesLedgerPage and
 * UserDetailsPage. Rather than add a fourth copy for the Prize Management tab,
 * it lives here once. (The existing three are untouched — worth folding in, but
 * that's not this change.)
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useImpersonateUser } from '@/api/extensions/useAdminOps';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import { authService } from '@/services/auth.service';
import { resetClientState } from '@/lib/resetClientState';
import { notify } from '@/utils/notifications';

export function useImpersonateUserFlow() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const impersonateUser = useImpersonateUser();
  const setAuthData = useAuthStore((s) => s.setAuthData);

  /** The admin's own profile id — never offer to impersonate yourself. */
  const currentUserProfileId = useAuthStore((s) => s.profile?.id);
  const role = useAuthStore((s) => s.role);
  const canImpersonate = role?.role_name === 'admin';

  const impersonate = useCallback(
    async (userProfileId: string) => {
      if (!userProfileId) return;
      try {
        const result = await impersonateUser.mutateAsync(userProfileId);
        resetClientState(queryClient);
        navigate('/dashboard', { replace: true });
        const session = await authService.validateSession();
        setAuthData({
          user: session.user,
          profile: session.profile,
          role: session.role,
          permissions: session.permissions,
          impersonator: session.impersonator ?? null,
        });
        notify.success(`Now impersonating ${result.targetName}`);
      } catch (err: any) {
        notify.error(err?.message || 'Failed to impersonate user');
      }
    },
    [impersonateUser, queryClient, navigate, setAuthData],
  );

  return {
    impersonate,
    isPending: impersonateUser.isPending,
    canImpersonate,
    currentUserProfileId,
  };
}
